import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { PatternCardProps } from "../../types/pattern";
import PatternPreview from "./PatternPreview";

const PatternCard: React.FC<PatternCardProps> = ({
  pattern,
  elementTypes,
  onEdit,
  onDelete,
  onToggleStatus,
  onUpload,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isFlipped, setIsFlipped] = useState(false);

  const elementType = elementTypes.find(
    (et) => et.id === pattern.element_type_id
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const handleCardClick = () => {
    setIsFlipped(!isFlipped);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };



  const renderSVGContent = (view: 'front' | 'side') => {
    const svgContent = view === 'front' ? pattern.front_svg_content : pattern.side_svg_content;

    if (svgContent) {
      // Check if the content is already a complete SVG or just fragments
      const isCompleteSVG = svgContent.trim().startsWith('<svg');

      let wrappedSVG;
      if (isCompleteSVG) {
        let processedSVG = svgContent;
        
        // For side view, always ensure good visibility
        if (view === 'side') {
          // Remove existing width/height attributes that might constrain the SVG
          processedSVG = processedSVG.replace(/\s+width="[^"]*"/g, '');
          processedSVG = processedSVG.replace(/\s+height="[^"]*"/g, '');
          
          // Add responsive width/height
          processedSVG = processedSVG.replace('<svg', '<svg width="100%" height="100%"');
          
          // Ensure proper styling for side view with centering and scaling
          const svgStyle = `
            max-width: 100%; 
            max-height: 100%; 
            display: block; 
            background: white; 
            margin: auto;
            transform: scale(2.2);
            transform-origin: center center;
            position: relative;
          `.replace(/\s+/g, ' ').trim();
          
          processedSVG = processedSVG.replace(
            '<svg',
            `<svg style="${svgStyle}"`
          );
        } else {
          // Front view - minimal styling
          const svgStyle = "max-width: 100%; max-height: 100%; display: block; background: white; margin: auto;";
          processedSVG = processedSVG.replace(
            '<svg',
            `<svg style="${svgStyle}"`
          );
        }

        wrappedSVG = processedSVG;
      } else {
        // Wrap SVG fragments in a proper SVG container
        const viewBox = view === 'side' ? "-100 -100 400 400" : "-200 -100 800 600";
        const svgStyle = view === 'side'
          ? "max-width: 100%; max-height: 100%; display: block; background: white; margin: auto; transform: scale(2.2); transform-origin: center center; position: relative;"
          : "max-width: 100%; max-height: 100%; display: block; background: white; margin: auto;";

        wrappedSVG = `
          <svg
            viewBox="${viewBox}"
            width="100%"
            height="100%"
            xmlns="http://www.w3.org/2000/svg"
            style="${svgStyle}"
          >
            ${svgContent}
          </svg>
        `;
      }

      // Container style optimized for display
      const containerStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        overflow: 'hidden',
        padding: view === 'side' ? '20px' : '5px', // More padding for side view
        position: 'relative' as const,
      };

      return (
        <div
          dangerouslySetInnerHTML={{ __html: wrappedSVG }}
          style={containerStyle}
        />
      );
    }

    // Fallback to PatternPreview component
    return (
      <PatternPreview
        pattern={pattern}
        view={view}
        size="medium"
        showFallback
      />
    );
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        opacity: pattern.status ? 1 : 0.7,
        border: pattern.status ? "none" : "1px dashed",
        borderColor: "warning.main",
      }}
    >
      {/* Preview Section */}
      <Box
        sx={{
          position: "relative",
          height: 200,
          bgcolor: "grey.50",
          cursor: "pointer",
          transition: "all 0.6s ease",
          transformStyle: "preserve-3d",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          "&:hover": {
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          },
        }}
        onClick={handleCardClick}
      >
        {/* Front Face */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backfaceVisibility: "hidden",
            transform: "rotateY(0deg)",
          }}
        >
          {renderSVGContent("front")}
        </Box>

        {/* Back Face */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          {renderSVGContent("side")}
        </Box>

        {/* View Toggle */}
        <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 10 }}>
          <Chip
            label={isFlipped ? "SIDE" : "FRONT"}
            size="small"
            color={isFlipped ? "secondary" : "primary"}
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.9)",
              "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
            }}
          />
        </Box>

        {/* Click to flip indicator */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            zIndex: 10,
            opacity: 0.7,
            "&:hover": { opacity: 1 },
          }}
        >
          <Chip
            label="Click to flip"
            size="small"
            variant="outlined"
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.8)",
              fontSize: "0.7rem",
            }}
          />
        </Box>

        {/* Status Indicator */}
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <Chip
            label={pattern.status ? "Active" : "Inactive"}
            size="small"
            color={pattern.status ? "success" : "warning"}
            variant={pattern.status ? "filled" : "outlined"}
          />
        </Box>

        {/* SVG Status Indicators */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            display: "flex",
            gap: 0.5,
          }}
        >
          <Tooltip
            title={`Front SVG: ${pattern.front_svg_content ? "Available" : "Missing"}`}
          >
            <Chip
              label="F"
              size="small"
              color={pattern.front_svg_content ? "success" : "error"}
              sx={{ minWidth: 24, height: 20, fontSize: "0.7rem" }}
            />
          </Tooltip>
          <Tooltip
            title={`Side SVG: ${pattern.side_svg_content ? "Available" : "Missing"}`}
          >
            <Chip
              label="S"
              size="small"
              color={pattern.side_svg_content ? "success" : "error"}
              sx={{ minWidth: 24, height: 20, fontSize: "0.7rem" }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Content Section */}
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Typography variant="h6" component="h3" gutterBottom noWrap>
          {pattern.description}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Element: {elementType?.name || "Unknown"}
        </Typography>

        {pattern.matching_string && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Matching: {pattern.matching_string}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" display="block">
          Created: {formatDate(pattern.created_at)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Positioning: {pattern.absolute_positioning === true ? 'Absolute' : pattern.absolute_positioning === false ? 'Relative' : 'Auto'}
        </Typography>
      </CardContent>

      <Divider />

      {/* Actions Section */}
      <CardActions sx={{ justifyContent: "space-between", px: 2, py: 1 }}>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Upload Front SVG">
            <IconButton
              size="small"
              onClick={() => onUpload(pattern, "front")}
              color={pattern.front_svg_content ? "success" : "default"}
            >
              <UploadIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Upload Side SVG">
            <IconButton
              size="small"
              onClick={() => onUpload(pattern, "side")}
              color={pattern.side_svg_content ? "success" : "default"}
            >
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box>
          <Tooltip title="Toggle Status">
            <IconButton
              size="small"
              onClick={() => onToggleStatus(pattern)}
              color={pattern.status ? "success" : "warning"}
            >
              {pattern.status ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>

          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </CardActions>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleAction(() => onEdit(pattern))}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Pattern</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => handleAction(() => onUpload(pattern, "front"))}
        >
          <ListItemIcon>
            <UploadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Upload Front SVG</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleAction(() => onUpload(pattern, "side"))}>
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Upload Side SVG</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => handleAction(() => onToggleStatus(pattern))}
          sx={{ color: pattern.status ? "warning.main" : "success.main" }}
        >
          <ListItemIcon>
            {pattern.status ? (
              <ToggleOffIcon fontSize="small" color="warning" />
            ) : (
              <ToggleOnIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>
            {pattern.status ? "Deactivate" : "Activate"}
          </ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => handleAction(() => onDelete(pattern))}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Pattern</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default PatternCard;